import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PortalInvitePreferencesCard from '../components/PortalInvitePreferencesCard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  User,
  Mail,
  Phone,
  Lock,
  Shield,
  ShieldCheck,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle,
  AlertCircle,
  Smartphone,
  Copy,
  Check,
  Monitor,
  Trash2,
  RefreshCw,
  Key,
  Globe,
  CreditCard,
  Package,
  Plus,
  Minus,
  AlertTriangle,
  ExternalLink,
  Receipt,
  Settings,
  ChevronRight,
  Users,
  Calculator,
  Truck,
  Sparkles,
  Settings2,
  Building2,
  MessageSquare,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import BusinessProfile from './BusinessProfile';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUPPORT_EMAIL = 'businessgenie.bg@gmail.com';
const SUPPORT_CATEGORIES = [
  { value: 'general', label: 'General Question' },
  { value: 'billing', label: 'Billing / Subscription' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'other', label: 'Other' },
];

// Category icons for modules
const categoryIcons = {
  core: Package,
  hr: Users,
  finance: Calculator,
  operations: Truck,
  compliance: Shield,
  integrations: Settings,
  general: Sparkles,
};

// Category colors for modules
const categoryColors = {
  core: 'bg-emerald-100 text-emerald-700',
  hr: 'bg-blue-100 text-blue-700',
  finance: 'bg-amber-100 text-amber-700',
  operations: 'bg-purple-100 text-purple-700',
  compliance: 'bg-red-100 text-red-700',
  integrations: 'bg-cyan-100 text-cyan-700',
  general: 'bg-gray-100 text-gray-700',
};

export default function ProfileSettings() {
  const { user, token, logout, setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  // Phase 19.23 — support-form state (merged in from AccountSettings)
  const [contactForm, setContactForm] = useState({
    category: 'general',
    subject: '',
    message: '',
  });
  const [sendingContact, setSendingContact] = useState(false);

  // Keep URL ?tab=… in sync with the active tab
  useEffect(() => {
    const current = searchParams.get('tab');
    if (current !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const sendContactSupport = async (e) => {
    e?.preventDefault?.();
    if (!contactForm.subject.trim() || !contactForm.message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSendingContact(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/support/contact`,
        contactForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || 'Message sent');
      setContactForm({ category: 'general', subject: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingContact(false);
    }
  };

  // Form states
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  // iter147 — dynamic business name for the header
  const [businessName, setBusinessName] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/business-profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setBusinessName(res.data?.company_name || res.data?.business_name || ''))
      .catch(() => {});
  }, [token]);

  // Email change states
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [changingEmail, setChangingEmail] = useState(false);

  // Password change states
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // 2FA states
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FADisable, setShow2FADisable] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [setting2FA, setSetting2FA] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  // Backup codes states
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [backupCodesCount, setBackupCodesCount] = useState({ remaining_codes: 0, total_codes: 0 });
  const [generatingCodes, setGeneratingCodes] = useState(false);

  // Session management states
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [revokingSession, setRevokingSession] = useState(null);

  // Billing states
  const [subscription, setSubscription] = useState(null);
  const [modules, setModules] = useState([]);
  const [moduleConfig, setModuleConfig] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showModulesDialog, setShowModulesDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [pendingModule, setPendingModule] = useState(null);
  const [savingModule, setSavingModule] = useState(null);

  useEffect(() => {
    fetchProfile();
    fetchSessions();
    fetchBackupCodesCount();
  }, []);

  // Fetch billing data when billing tab is active
  useEffect(() => {
    if (activeTab === 'billing') {
      fetchBillingData();
    }
  }, [activeTab]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(response.data);
      setName(response.data.name || '');
      setDisplayName(response.data.display_name || '');
      setPhone(response.data.phone || '');
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const response = await axios.get(`${API_URL}/api/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const fetchBackupCodesCount = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/profile/2fa/backup-codes/count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBackupCodesCount(response.data);
    } catch (error) {
      console.error('Error fetching backup codes count:', error);
    }
  };

  const fetchBillingData = async () => {
    setLoadingBilling(true);
    try {
      const [subRes, modulesRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/status`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/modules/available`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/modules/config`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setSubscription(subRes.data);
      setModules(modulesRes.data);
      setModuleConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Failed to load billing information');
    } finally {
      setLoadingBilling(false);
    }
  };

  const handleGenerateBackupCodes = async () => {
    setGeneratingCodes(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/profile/2fa/backup-codes/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBackupCodes(response.data.codes);
      setShowBackupCodes(true);
      fetchBackupCodesCount();
      toast.success('Backup codes generated! Save them in a safe place.');
    } catch (error) {
      console.error('Error generating backup codes:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate backup codes');
    } finally {
      setGeneratingCodes(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    setRevokingSession(sessionId);
    try {
      await axios.delete(`${API_URL}/api/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Session revoked');
      fetchSessions();
    } catch (error) {
      console.error('Error revoking session:', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke session');
    } finally {
      setRevokingSession(null);
    }
  };

  const handleRevokeAllOtherSessions = async () => {
    try {
      const response = await axios.delete(`${API_URL}/api/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Revoked ${response.data.revoked_count} session(s)`);
      fetchSessions();
    } catch (error) {
      console.error('Error revoking sessions:', error);
      toast.error('Failed to revoke sessions');
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/profile`,
        { name, display_name: displayName, phone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profile updated successfully');
      fetchProfile();
      // Phase 19.24 — propagate display_name update to AuthContext so sidebar/greeting refresh immediately
      if (setUser && user) {
        setUser({ ...user, name, display_name: displayName || null });
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail || !emailPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    setChangingEmail(true);
    try {
      await axios.post(
        `${API_URL}/api/profile/change-email`,
        { new_email: newEmail, password: emailPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Verification email sent! Check your inbox.');
      setShowEmailDialog(false);
      setNewEmail('');
      setEmailPassword('');
    } catch (error) {
      console.error('Error changing email:', error);
      toast.error(error.response?.data?.detail || 'Failed to change email');
    } finally {
      setChangingEmail(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(
        `${API_URL}/api/profile/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSetup2FA = async () => {
    setSetting2FA(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/profile/2fa/setup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrCode(response.data.qr_code);
      setManualKey(response.data.manual_entry_key);
      setShow2FASetup(true);
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setSetting2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setSetting2FA(true);
    try {
      await axios.post(
        `${API_URL}/api/profile/2fa/verify`,
        { code: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Two-factor authentication enabled!');
      setShow2FASetup(false);
      setVerificationCode('');
      fetchProfile();
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setSetting2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword || !disableCode) {
      toast.error('Please fill in all fields');
      return;
    }

    setSetting2FA(true);
    try {
      await axios.post(
        `${API_URL}/api/profile/2fa/disable`,
        { password: disablePassword, code: disableCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Two-factor authentication disabled');
      setShow2FADisable(false);
      setDisablePassword('');
      setDisableCode('');
      fetchProfile();
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setSetting2FA(false);
    }
  };

  const copyManualKey = () => {
    navigator.clipboard.writeText(manualKey);
    setCopiedKey(true);
    toast.success('Key copied to clipboard');
    setTimeout(() => setCopiedKey(false), 2000);
  };

  // Billing handlers
  const handleOpenBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/subscriptions/portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error('Failed to open billing portal');
      setOpeningPortal(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await axios.post(
        `${API_URL}/api/subscriptions/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Subscription cancelled. You will have access until the end of your billing period.');
      setShowCancelDialog(false);
      fetchBillingData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await axios.post(
        `${API_URL}/api/subscriptions/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Subscription reactivated!');
      fetchBillingData();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    }
  };

  const isModuleEnabled = (moduleId) => {
    return moduleConfig?.enabled_modules?.some(m => m.module_id === moduleId) || false;
  };

  const handleToggleModule = async (module) => {
    if (module.is_base) {
      toast.error('Base modules cannot be modified');
      return;
    }

    setPendingModule({
      ...module,
      action: isModuleEnabled(module.id) ? 'disable' : 'enable'
    });
  };

  const confirmModuleChange = async () => {
    if (!pendingModule) return;

    setSavingModule(pendingModule.id);

    try {
      const endpoint = pendingModule.action === 'enable'
        ? '/api/modules/enable'
        : '/api/modules/disable';

      await axios.post(
        `${API_URL}${endpoint}`,
        { module_id: pendingModule.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        pendingModule.action === 'enable'
          ? `${pendingModule.name} added to your subscription`
          : `${pendingModule.name} removed from your subscription`
      );

      fetchBillingData();
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error(error.response?.data?.detail || 'Failed to update module');
    } finally {
      setSavingModule(null);
      setPendingModule(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'trialing':
        return 'bg-blue-100 text-blue-700';
      case 'past_due':
        return 'bg-red-100 text-red-700';
      case 'canceled':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="settings-page-heading">
          <Building2 className="h-8 w-8 text-primary" />
          <span>
            {businessName ? businessName : 'Your Business'}
            <span className="text-muted-foreground font-medium text-lg"> — Settings</span>
          </span>
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage your business profile, personal info, security, sessions, and billing
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 h-auto gap-1 p-1" data-testid="settings-tabs">
          <TabsTrigger value="profile" className="flex items-center gap-2" data-testid="profile-tab">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Profile</span>
          </TabsTrigger>
          <TabsTrigger value="business" className="flex items-center gap-2" data-testid="business-tab">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Business</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2" data-testid="security-tab">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Security</span>
          </TabsTrigger>
          <TabsTrigger value="sessions" className="flex items-center gap-2" data-testid="sessions-tab">
            <Monitor className="h-4 w-4" />
            <span className="hidden sm:inline">Sessions</span>
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center gap-2" data-testid="billing-tab">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Billing</span>
          </TabsTrigger>
          <TabsTrigger value="support" className="flex items-center gap-2" data-testid="support-tab">
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Support</span>
          </TabsTrigger>
        </TabsList>

        {/* Business Profile Tab — embeds the BusinessProfile editor + Guided Tour restart */}
        <TabsContent value="business" className="space-y-6">
          {/* Guided Business Tour — sub-section pinned above the profile editor */}
          <Card data-testid="guided-tour-subsection">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-4 w-4 text-emerald-600" />
                Guided Business Tour
              </CardTitle>
              <CardDescription>
                Re-run the 4-step guided tour anytime to update your business type, focus areas,
                or install a different starter template. Your existing data is safe — the tour
                only adds new job statuses / custom fields that don&apos;t already exist.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={() => window.dispatchEvent(new CustomEvent("bg:open-onboarding-tour"))}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                data-testid="restart-guided-tour-btn"
              >
                <Sparkles className="h-4 w-4" /> Restart Guided Tour
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/customization")}
                className="gap-2"
                data-testid="open-customize-business-btn"
              >
                <Settings2 className="h-4 w-4" /> Open Customize Business
              </Button>
            </CardContent>
          </Card>

          <div className="-mx-4 sm:-mx-6" data-testid="business-profile-tab-content">
            <BusinessProfile />
          </div>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Personal Information
              </CardTitle>
              <CardDescription>
                Update your personal details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+1 (555) 000-0000"
                      className="pl-10"
                      data-testid="profile-phone-input"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="display_name">Display Name</Label>
                <Input
                  id="display_name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={name ? name.split(' ')[0] : 'Admin'}
                  maxLength={50}
                  data-testid="profile-display-name-input"
                />
                <p className="text-xs text-muted-foreground">
                  Shown in the sidebar and dashboard greeting. Leave blank to use your first name.
                </p>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-btn">
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Email Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Address
              </CardTitle>
              <CardDescription>
                Your email is used for login and notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{profile?.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {profile?.is_email_verified ? (
                        <Badge variant="outline" className="text-green-600 border-green-600">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-600">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Not Verified
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <Button variant="outline" onClick={() => setShowEmailDialog(true)} data-testid="change-email-btn">
                  Change Email
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Account ID</span>
                  <span className="font-mono">{profile?.id?.slice(0, 8)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Role</span>
                  <Badge variant="secondary">{profile?.role}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Account Type</span>
                  <span>{profile?.account_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Member Since</span>
                  <span>{profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Phase 19.23 — Portal Invitation Preferences */}
          <PortalInvitePreferencesCard />
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6">
          {/* Password */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Password
              </CardTitle>
              <CardDescription>
                Manage your password settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Lock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">Password</p>
                    <p className="text-sm text-muted-foreground">
                      Last changed: Unknown
                    </p>
                  </div>
                </div>
                <Button variant="outline" onClick={() => setShowPasswordDialog(true)} data-testid="change-password-btn">
                  Change Password
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Two-Factor Authentication */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Two-Factor Authentication
              </CardTitle>
              <CardDescription>
                Add an extra layer of security to your account
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    profile?.is_2fa_enabled ? 'bg-green-100' : 'bg-muted'
                  }`}>
                    {profile?.is_2fa_enabled ? (
                      <ShieldCheck className="h-5 w-5 text-green-600" />
                    ) : (
                      <Smartphone className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">Two-Factor Authentication</p>
                    <p className="text-sm text-muted-foreground">
                      {profile?.is_2fa_enabled
                        ? 'Your account is protected with 2FA'
                        : 'Add an extra layer of security to your account'
                      }
                    </p>
                  </div>
                </div>
                {profile?.is_2fa_enabled ? (
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setShow2FADisable(true)}
                    data-testid="disable-2fa-btn"
                  >
                    Disable 2FA
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleSetup2FA}
                    disabled={setting2FA}
                    data-testid="enable-2fa-btn"
                  >
                    {setting2FA ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ShieldCheck className="mr-2 h-4 w-4" />
                    )}
                    Enable 2FA
                  </Button>
                )}
              </div>

              {/* Backup Codes - Only show when 2FA is enabled */}
              {profile?.is_2fa_enabled && (
                <>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Key className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">Backup Codes</p>
                        <p className="text-sm text-muted-foreground">
                          {backupCodesCount.remaining_codes} of {backupCodesCount.total_codes} codes remaining
                        </p>
                        {backupCodesCount.should_regenerate && (
                          <p className="text-sm text-amber-600 flex items-center gap-1 mt-1">
                            <AlertCircle className="h-4 w-4" />
                            Running low! Generate new codes.
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={handleGenerateBackupCodes}
                      disabled={generatingCodes}
                      data-testid="generate-backup-codes-btn"
                    >
                      {generatingCodes ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-2 h-4 w-4" />
                      )}
                      {backupCodesCount.total_codes > 0 ? 'Regenerate Codes' : 'Generate Codes'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Monitor className="h-5 w-5" />
                    Active Sessions
                  </CardTitle>
                  <CardDescription>
                    Manage devices where you're signed in
                  </CardDescription>
                </div>
                {sessions.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={handleRevokeAllOtherSessions}
                    data-testid="revoke-all-sessions-btn"
                  >
                    Sign out all other devices
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loadingSessions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Monitor className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No active sessions found</p>
                  <p className="text-sm">Sessions will appear here after you sign in</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        session.is_current ? 'bg-primary/5 border-primary/20' : 'bg-muted/30'
                      }`}
                      data-testid={`session-item-${session.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          session.is_current ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                        }`}>
                          {session.device_info?.toLowerCase().includes('mobile') ? (
                            <Smartphone className="h-4 w-4" />
                          ) : (
                            <Monitor className="h-4 w-4" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{session.device_info || 'Unknown device'}</p>
                            {session.is_current && (
                              <Badge variant="default" className="text-xs">Current</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {session.ip_address && (
                              <span className="flex items-center gap-1">
                                <Globe className="h-3 w-3" />
                                {session.ip_address}
                              </span>
                            )}
                            <span>
                              Last active: {session.last_used_at
                                ? new Date(session.last_used_at).toLocaleString()
                                : 'Unknown'}
                            </span>
                          </div>
                        </div>
                      </div>
                      {!session.is_current && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRevokeSession(session.id)}
                          disabled={revokingSession === session.id}
                          data-testid={`revoke-session-btn-${session.id}`}
                        >
                          {revokingSession === session.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-6">
          {loadingBilling ? (
            <div className="flex items-center justify-center min-h-[300px]">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Subscription Status Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Subscription Status</CardTitle>
                      <CardDescription>Your current plan and billing cycle</CardDescription>
                    </div>
                    {subscription?.status && (
                      <Badge className={getStatusColor(subscription.status)}>
                        {subscription.status === 'trialing' ? 'Free Trial' : subscription.status}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Plan Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Current Plan</p>
                      <p className="font-semibold capitalize">{subscription?.plan || 'None'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Monthly Cost</p>
                      <p className="font-semibold">${moduleConfig?.total_monthly_cost?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Active Modules</p>
                      <p className="font-semibold">{moduleConfig?.enabled_modules?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {subscription?.status === 'trialing' ? 'Trial Ends' : 'Next Billing'}
                      </p>
                      <p className="font-semibold">
                        {subscription?.status === 'trialing'
                          ? formatDate(subscription?.trial_ends_at)
                          : formatDate(subscription?.current_period_end)}
                      </p>
                    </div>
                  </div>

                  {/* Warning for cancellation */}
                  {subscription?.cancel_at_period_end && (
                    <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertTriangle className="h-5 w-5 text-amber-600" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-800">Subscription Ending</p>
                        <p className="text-sm text-amber-700">
                          Your subscription will end on {formatDate(subscription?.current_period_end)}.
                          You can reactivate anytime before then.
                        </p>
                      </div>
                      <Button size="sm" onClick={handleReactivateSubscription} data-testid="reactivate-subscription-btn">
                        Reactivate
                      </Button>
                    </div>
                  )}

                  <Separator />

                  {/* Actions */}
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={handleOpenBillingPortal} disabled={openingPortal} data-testid="manage-payment-btn">
                      {openingPortal ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="mr-2 h-4 w-4" />
                      )}
                      Manage Payment Methods
                    </Button>

                    <Button variant="outline" onClick={handleOpenBillingPortal} disabled={openingPortal} data-testid="view-invoices-btn">
                      <Receipt className="mr-2 h-4 w-4" />
                      View Invoices
                    </Button>

                    {subscription?.status === 'active' && !subscription?.cancel_at_period_end && subscription?.plan !== 'legacy' && (
                      <Button
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setShowCancelDialog(true)}
                        data-testid="cancel-subscription-btn"
                      >
                        Cancel Subscription
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Active Modules Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        Your Modules
                      </CardTitle>
                      <CardDescription>
                        Features included in your subscription
                      </CardDescription>
                    </div>
                    <Button onClick={() => setShowModulesDialog(true)} data-testid="manage-modules-btn">
                      <Settings className="mr-2 h-4 w-4" />
                      Manage Modules
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {moduleConfig?.enabled_modules?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No modules enabled</p>
                      <p className="text-sm">Add modules to customize your subscription</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {moduleConfig?.enabled_modules?.map((mod) => {
                        const fullModule = modules.find(m => m.id === mod.module_id);
                        const CategoryIcon = categoryIcons[fullModule?.category] || Package;
                        const colorClass = categoryColors[fullModule?.category] || categoryColors.general;

                        return (
                          <div
                            key={mod.module_id}
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                            data-testid={`module-item-${mod.module_id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${colorClass}`}>
                                <CategoryIcon className="h-4 w-4" />
                              </div>
                              <div>
                                <p className="font-medium">{mod.module_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  ${mod.price?.toFixed(2)}/month
                                </p>
                              </div>
                            </div>
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              <Check className="mr-1 h-3 w-3" />
                              Active
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Links */}
              <Card>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        Need help? Visit our billing FAQ or contact support
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleOpenBillingPortal} data-testid="stripe-portal-link">
                      Open Stripe Portal
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Support Tab — merged in from AccountSettings (Phase 19.23) */}
        <TabsContent value="support" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Contact Business Genie
              </CardTitle>
              <CardDescription>
                Send a question or comment to{' '}
                <span className="font-mono text-foreground">{SUPPORT_EMAIL}</span>. We typically reply within 1 business day.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={sendContactSupport} data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Category</Label>
                    <Select
                      value={contactForm.category}
                      onValueChange={(v) => setContactForm({ ...contactForm, category: v })}
                    >
                      <SelectTrigger data-testid="contact-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SUPPORT_CATEGORIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Replying to</Label>
                    <Input value={user?.email || ''} disabled />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Subject *</Label>
                  <Input
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    placeholder="What's this about?"
                    data-testid="contact-subject-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Message *</Label>
                  <Textarea
                    rows={6}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    placeholder="Tell us what you need..."
                    data-testid="contact-message-input"
                  />
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={sendingContact} data-testid="send-contact-btn">
                    {sendingContact ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send Message
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ DIALOGS ============ */}

      {/* Change Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Email Address</DialogTitle>
            <DialogDescription>
              Enter your new email address and current password to verify
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Email Address</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="newemail@example.com"
                data-testid="new-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={emailPassword}
                onChange={(e) => setEmailPassword(e.target.value)}
                placeholder="Enter your current password"
                data-testid="email-change-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleChangeEmail} disabled={changingEmail} data-testid="confirm-email-change-btn">
              {changingEmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Send Verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                data-testid="confirm-new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleChangePassword} disabled={changingPassword} data-testid="confirm-password-change-btn">
              {changingPassword ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* QR Code */}
            <div className="flex justify-center">
              {qrCode && (
                <img src={qrCode} alt="2FA QR Code" className="w-48 h-48 border rounded-lg" />
              )}
            </div>

            {/* Manual Entry Key */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">
                Or enter this key manually:
              </Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-muted rounded text-sm font-mono break-all">
                  {manualKey}
                </code>
                <Button variant="outline" size="icon" onClick={copyManualKey}>
                  {copiedKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Verification Code */}
            <div className="space-y-2">
              <Label>Enter verification code from your app</Label>
              <Input
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                data-testid="2fa-verification-code-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow2FASetup(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleVerify2FA}
              disabled={setting2FA || verificationCode.length !== 6}
              data-testid="verify-2fa-btn"
            >
              {setting2FA ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Enable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={show2FADisable} onOpenChange={setShow2FADisable}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              This will make your account less secure. Enter your password and current 2FA code to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="Enter your password"
                data-testid="disable-2fa-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>2FA Code</Label>
              <Input
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center tracking-widest font-mono"
                maxLength={6}
                data-testid="disable-2fa-code-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow2FADisable(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisable2FA}
              disabled={setting2FA}
              data-testid="confirm-disable-2fa-btn"
            >
              {setting2FA ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Disable 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Your Backup Codes
            </DialogTitle>
            <DialogDescription>
              Save these codes in a safe place. Each code can only be used once.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                <strong>Important:</strong> These codes will only be shown once!
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <div
                  key={index}
                  className="bg-muted p-2 rounded font-mono text-center text-sm tracking-wider"
                >
                  {code}
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                const text = backupCodes.join('\n');
                navigator.clipboard.writeText(text);
                toast.success('Backup codes copied to clipboard');
              }}
              data-testid="copy-backup-codes-btn"
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy All Codes
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowBackupCodes(false)} data-testid="close-backup-codes-btn">
              I've Saved My Codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Cancel Subscription?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>Are you sure you want to cancel your subscription?</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>You'll retain access until {formatDate(subscription?.current_period_end)}</li>
                <li>All your data will be preserved</li>
                <li>You can reactivate anytime before the end date</li>
                <li>After the end date, your account will be downgraded</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleCancelSubscription}
              disabled={cancelling}
            >
              {cancelling ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Yes, Cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage Modules Dialog */}
      <Dialog open={showModulesDialog} onOpenChange={setShowModulesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Manage Modules
            </DialogTitle>
            <DialogDescription>
              Add or remove modules to customize your subscription
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {modules.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No modules available</p>
                <p className="text-sm">Contact support to set up your modules</p>
              </div>
            ) : (
              modules.map((module) => {
                const enabled = isModuleEnabled(module.id);
                const CategoryIcon = categoryIcons[module.category] || Package;
                const colorClass = categoryColors[module.category] || categoryColors.general;
                const isSaving = savingModule === module.id;

                return (
                  <div
                    key={module.id}
                    className={`p-4 rounded-lg border ${enabled ? 'bg-primary/5 border-primary/20' : 'bg-muted/30'}`}
                    data-testid={`module-config-${module.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${colorClass}`}>
                          <CategoryIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold">{module.name}</h4>
                            {module.is_base && (
                              <Badge variant="secondary" className="text-xs">Required</Badge>
                            )}
                          </div>
                          {module.description && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {module.description}
                            </p>
                          )}
                          {module.features?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {module.features.slice(0, 4).map((f) => (
                                <Badge key={f} variant="outline" className="text-xs">
                                  {f.replace(/_/g, ' ')}
                                </Badge>
                              ))}
                              {module.features.length > 4 && (
                                <Badge variant="outline" className="text-xs">
                                  +{module.features.length - 4} more
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <p className="font-semibold">
                          ${module.price?.toFixed(2)}
                          <span className="text-sm font-normal text-muted-foreground">
                            /{module.interval}
                          </span>
                        </p>
                        {module.is_base ? (
                          <Badge className="bg-green-100 text-green-700">
                            <Check className="mr-1 h-3 w-3" />
                            Included
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant={enabled ? "outline" : "default"}
                            onClick={() => handleToggleModule(module)}
                            disabled={isSaving}
                            data-testid={`toggle-module-${module.id}`}
                          >
                            {isSaving ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : enabled ? (
                              <>
                                <Minus className="mr-1 h-4 w-4" />
                                Remove
                              </>
                            ) : (
                              <>
                                <Plus className="mr-1 h-4 w-4" />
                                Add
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <div className="flex items-center justify-between w-full">
              <div>
                <p className="text-sm text-muted-foreground">New Monthly Total</p>
                <p className="text-xl font-bold">
                  ${moduleConfig?.total_monthly_cost?.toFixed(2) || '0.00'}
                </p>
              </div>
              <Button onClick={() => setShowModulesDialog(false)} data-testid="close-modules-dialog">
                Done
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Module Change Dialog */}
      <AlertDialog open={!!pendingModule} onOpenChange={() => setPendingModule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingModule?.action === 'enable' ? 'Add' : 'Remove'} {pendingModule?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingModule?.action === 'enable' ? (
                <>
                  This will add <strong>${pendingModule?.price?.toFixed(2)}/month</strong> to your subscription.
                  You'll be charged on your next billing date.
                </>
              ) : (
                <>
                  This will remove <strong>${pendingModule?.price?.toFixed(2)}/month</strong> from your subscription.
                  You'll lose access to features provided by this module.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmModuleChange}
              className={pendingModule?.action === 'disable' ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid="confirm-module-change-btn"
            >
              {pendingModule?.action === 'enable' ? 'Add Module' : 'Remove Module'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
